import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import SubmitPanel from './SubmitPanel'
import ChatLogSubmitPanel from './ChatLogSubmitPanel'
import LabReportSubmitPanel from './LabReportSubmitPanel'
import {
  STATUS,
  archiveDisplayTitle,
  deadlineUrgency,
  formatDateTime,
  getAllowedAIIDEs,
  normalizeStatus,
  processHtmlContent,
  scenarioName,
  statusMeta,
  trainingTypeLabel,
} from './homeworkUtils'
import { homeworkPathPrefix } from '../CoursePortal/constants'
import { EDUGATE_API_URL } from '../../config/serviceConfig'
import { YouLabberLocalDownloadCard } from '../CCPanel/YouLabberLocalDownloadEntry'

export default function TaskDetail({
  homework,
  orderedList = [],
  archivesList = [],
  archivesLoading = false,
  archivesError = '',
  draft = {},
  onDraftChange,
  onSubmitArchive,
  submitting = false,
  justSubmittedId = null,
  onClearJustSubmitted,
  onSelectHomework,
  onOpenEvaluation,
  onStartTraining,
  isFromMenu = false,
  getStudentId,
  buildIdentityHeaders,
  onRefresh,
  onRequestArchives,
}) {
  const { t } = useTranslation()
  const [contentExpanded, setContentExpanded] = useState(false)
  const [resourcesOpen, setResourcesOpen] = useState(false)
  const [replacing, setReplacing] = useState(false)
  const [ocrPending, setOcrPending] = useState(false)

  const status = normalizeStatus(homework?.status)
  const meta = statusMeta(status, t)
  const urgency = deadlineUrgency(homework?.deadline)
  const resourceInsights = Array.isArray(homework?.resourceInsights) ? homework.resourceInsights : []
  const hasYouLaber = Boolean(homework?.youlaberSkillDownloadUrl)
  const html = useMemo(
    () => processHtmlContent(homework?.content || homework?.Content || '', EDUGATE_API_URL),
    [homework],
  )
  const longContent = String(homework?.content || '').replace(/<[^>]*>/g, '').trim().length > 220

  const index = orderedList.findIndex((item) => Number(item.id) === Number(homework?.id))
  const prev = index > 0 ? orderedList[index - 1] : null
  const next = index >= 0 && index < orderedList.length - 1 ? orderedList[index + 1] : null

  const submittedArchiveTitle = useMemo(() => {
    if (!homework?.archiveId || !archivesList) return ''
    const found = archivesList.find((a) => a.archiveId === homework.archiveId)
    return found ? archiveDisplayTitle(found, t) : t('hw_unknown_project')
  }, [homework?.archiveId, archivesList, t])

  const showSuccess = justSubmittedId && Number(justSubmittedId) === Number(homework?.id)
  const isChatlogs = homework?.trainingType === 'chatlogs'
  const isLabReport = homework?.trainingType === 'lab_report'
  const needsLocalClient = isChatlogs && getAllowedAIIDEs(homework).some((ide) => String(ide).toLowerCase() === 'youlaber')
  const showSubmitPanel = status === STATUS.UNSUBMITTED || replacing

  useEffect(() => {
    setContentExpanded(false)
    setResourcesOpen(false)
    setReplacing(false)
    setOcrPending(false)
    if (!isChatlogs && !isLabReport) onRequestArchives?.(homework)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homework?.id])

  useEffect(() => {
    if (replacing && !isChatlogs && !isLabReport) onRequestArchives?.(homework)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replacing])

  const collapseRequirement = (status === STATUS.SUBMITTED || status === STATUS.EVALUATED) && !contentExpanded
  const pathPrefix = homeworkPathPrefix(homework, homework.timelineUnit)

  return (
    <div>
      <div className="hwb-detail-head">
        <div className="hwb-detail-head-row">
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 className="hwb-detail-title">
              {pathPrefix ? <span className="hwb-path-chip">{pathPrefix}</span> : null}
              {homework.title || homework.Title}
            </h2>
            <div className="hwb-badges">
              <span className={`hwb-badge ${meta.tone}`}>{meta.text}</span>
              <span className="hwb-badge">{trainingTypeLabel(homework.trainingType, t)}</span>
              {(homework.courseName || homework.CourseName) ? (
                <span className="hwb-badge">{homework.courseName || homework.CourseName}</span>
              ) : null}
              {urgency === 'soon' ? <span className="hwb-badge soon">即将截止</span> : null}
              {urgency === 'overdue' ? <span className="hwb-badge overdue">已过期</span> : null}
            </div>
          </div>
          <div className="hwb-nav-btns">
            <button type="button" disabled={!prev} onClick={() => onSelectHomework?.(prev)} aria-label="上一份作业">‹</button>
            <span className="hwb-nav-pos">{index >= 0 ? `${index + 1} / ${orderedList.length}` : '-'}</span>
            <button type="button" disabled={!next} onClick={() => onSelectHomework?.(next)} aria-label="下一份作业">›</button>
          </div>
        </div>
      </div>

      <section className="hwb-section">
        <div className="hwb-meta-grid">
          <div className="hwb-meta-item">
            <label>{t('hw_deadline')}</label>
            <span>{formatDateTime(homework.deadline)}</span>
          </div>
          <div className="hwb-meta-item">
            <label>{t('hw_scenario_name')}</label>
            <span>{scenarioName(homework, t)}</span>
          </div>
          {homework.teacherScore != null && status === STATUS.EVALUATED ? (
            <div className="hwb-meta-item">
              <label>{t('hw_teacher_score')}</label>
              <span>{homework.teacherScore}</span>
            </div>
          ) : null}
        </div>

        {collapseRequirement ? (
          <button type="button" className="hwb-link-btn" onClick={() => setContentExpanded(true)}>
            查看任务要求
          </button>
        ) : (
          <>
            <p className="hwb-section-title">{t('hw_training_content')}</p>
            <div
              className={`hwb-content${!contentExpanded && longContent ? ' is-clamped' : ''}`}
              dangerouslySetInnerHTML={{ __html: html || '<p>暂无任务说明</p>' }}
            />
            {longContent ? (
              <button type="button" className="hwb-link-btn" onClick={() => setContentExpanded((v) => !v)}>
                {contentExpanded ? '收起全文' : '展开全文'}
              </button>
            ) : null}
          </>
        )}

        {needsLocalClient ? <YouLabberLocalDownloadCard /> : null}

        {(resourceInsights.length > 0 || hasYouLaber) ? (
          <div style={{ marginTop: 12 }}>
            <button type="button" className="hwb-link-btn" onClick={() => setResourcesOpen((v) => !v)}>
              {resourcesOpen
                ? '收起资源包'
                : `本任务资源包（${resourceInsights.length + (hasYouLaber ? 1 : 0)} 项）`}
            </button>
            {resourcesOpen ? (
              <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
                {resourceInsights.map((item, idx) => (
                  <div key={`${item.resourceType}-${idx}`} className="hwb-section" style={{ margin: 0, padding: 12 }}>
                    <div style={{ fontSize: 12, color: 'var(--hwb-dim)', marginBottom: 4 }}>
                      {item.resourceLabel || '资源'} {item.version || ''}
                    </div>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>{item.title || '未命名资源'}</div>
                    <div style={{ fontSize: 12, color: 'var(--hwb-soft)', lineHeight: 1.55 }}>{item.summary}</div>
                    {item.downloadUrl ? (
                      <a href={item.downloadUrl} target="_blank" rel="noreferrer" className="hwb-link-btn" style={{ display: 'inline-block' }}>
                        下载 Skill 包
                      </a>
                    ) : null}
                  </div>
                ))}
                {hasYouLaber ? (
                  <div className="hwb-section" style={{ margin: 0, padding: 12 }}>
                    <div style={{ fontSize: 12, color: 'var(--hwb-dim)', marginBottom: 4 }}>
                      YouLaber Skill {homework.youlaberSkillVersion || ''}
                    </div>
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>{homework.youlaberSkillName || 'YouLabber Skill'}</div>
                    <a
                      href={homework.youlaberSkillDownloadUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="hwb-link-btn"
                      style={{ display: 'inline-block' }}
                    >
                      下载本任务授权 Skill
                    </a>
                    <ol style={{ fontSize: 12, color: 'var(--hwb-soft)', lineHeight: 1.7, margin: '8px 0 0', paddingLeft: 18 }}>
                      <li>
                        把下载的压缩包解压到 <code>C:\ybg-workspace\skills\</code>，解压后应存在
                        {' '}<code>C:\ybg-workspace\skills\{homework.youlaberSkillKey || '<skill名>'}\SKILL.md</code>
                        （多一层或少一层目录都不行）。
                      </li>
                      <li>
                        若任务栏里已有 YouLaber，先关掉窗口。到 <strong>YouLaber.exe 所在文件夹</strong>双击
                        {' '}<code>reconfig.cmd</code>，在「请选择本机 YouLabber 实验员类型」中选择
                        {' '}<strong>{homework.youlaberDeviceType || '教师在本作业中指定的类型'}</strong>
                        （换作业或本机以前配过别的类型时都要重选）。完成后不要关这个窗口，本机实验员会在里面继续运行。
                      </li>
                      <li>用浏览器登录邮百工，切换到顶部「YouLaber」标签页开始实验。</li>
                      {homework.youlaberMinVersion ? (
                        <li>本机 YouLaber 需 {homework.youlaberMinVersion} 或更高版本。</li>
                      ) : null}
                      {homework.youlaberSkillChecksum ? (
                        <li style={{ wordBreak: 'break-all' }}>校验值：<code>{homework.youlaberSkillChecksum}</code></li>
                      ) : null}
                    </ol>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </section>

      {/* 行动区 */}
      {showSuccess || (status === STATUS.SUBMITTED && !replacing) ? (
        <section className="hwb-action is-ok">
          <h3 className="hwb-action-title">
            {ocrPending ? '已提交，正在识别文档内容并排队评价' : (showSuccess ? '提交成功' : '已提交，等待评价')}
          </h3>
          <div className="hwb-receipt-row">
            <span>存档：<strong>{submittedArchiveTitle || '-'}</strong></span>
            <span>说明：<strong>{homework.submitMessage || t('hw_no_message')}</strong></span>
          </div>
          <div className="hwb-secondary-actions">
            <button
              type="button"
              className="hwb-btn hwb-btn-secondary"
              onClick={() => {
                onClearJustSubmitted?.()
                setReplacing(true)
              }}
            >
              重新提交作业
            </button>
            {status === STATUS.SUBMITTED || status === STATUS.EVALUATED ? (
              <button type="button" className="hwb-btn hwb-btn-secondary" onClick={() => onOpenEvaluation?.(homework)}>
                {t('hw_view_eval')}
              </button>
            ) : null}
          </div>
        </section>
      ) : null}

      {status === STATUS.EVALUATED && !replacing ? (
        <section className="hwb-action is-ok">
          <h3 className="hwb-action-title">评价结果</h3>
          <div className="hwb-score-hero">
            {homework.teacherScore != null ? <span className="score">{homework.teacherScore}</span> : null}
            <span className="level">{meta.text}</span>
          </div>
          {homework.teacherComment ? (
            <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--hwb-soft)', lineHeight: 1.55 }}>
              {homework.teacherComment}
            </p>
          ) : null}
          <div className="hwb-secondary-actions">
            <button type="button" className="hwb-btn hwb-btn-primary" onClick={() => onOpenEvaluation?.(homework)}>
              查看完整评价
            </button>
            <button type="button" className="hwb-btn hwb-btn-secondary" onClick={() => setReplacing(true)}>
              重新提交作业
            </button>
          </div>
        </section>
      ) : null}

      {status === STATUS.REJECTED && !replacing ? (
        <section className="hwb-action is-danger">
          <h3 className="hwb-action-title">已打回，请修改后重新提交</h3>
          {homework.teacherComment ? (
            <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--hwb-soft)', lineHeight: 1.55 }}>
              教师意见：{homework.teacherComment}
            </p>
          ) : null}
          <button type="button" className="hwb-btn hwb-btn-primary" onClick={() => setReplacing(true)}>
            重新选择存档提交
          </button>
        </section>
      ) : null}

      {showSubmitPanel ? (
        <section className="hwb-action">
          <h3 className="hwb-action-title">
            {isChatlogs ? '上传 ChatLogs 提交' : (isLabReport ? '上传实验报告' : (replacing ? '更换提交存档' : '选择存档并提交'))}
          </h3>
          {isChatlogs ? (
            <ChatLogSubmitPanel
              homework={homework}
              draft={draft}
              onDraftChange={onDraftChange}
              getStudentId={getStudentId}
              buildIdentityHeaders={buildIdentityHeaders}
              onSubmitted={() => {
                setReplacing(false)
                onRefresh?.()
              }}
            />
          ) : isLabReport ? (
            <LabReportSubmitPanel
              homework={homework}
              draft={draft}
              onDraftChange={onDraftChange}
              getStudentId={getStudentId}
              buildIdentityHeaders={buildIdentityHeaders}
              onSubmitted={(payload) => {
                setOcrPending(payload?.ocrStatus === 'pending')
                setReplacing(false)
                onRefresh?.()
              }}
            />
          ) : (
            <SubmitPanel
              homework={homework}
              archivesList={archivesList}
              archivesLoading={archivesLoading}
              archivesError={archivesError}
              draft={draft}
              onDraftChange={onDraftChange}
              submitting={submitting}
              mode={replacing ? 'replace' : 'submit'}
              onSubmit={async (archiveId, message) => {
                const ok = await onSubmitArchive?.(homework.id, archiveId, message)
                if (ok !== false) setReplacing(false)
                return ok
              }}
              onRetryArchives={() => onRequestArchives?.(homework)}
            />
          )}
        </section>
      ) : null}

      <div className="hwb-secondary-actions">
        {!isFromMenu && homework.trainingType !== 'chatlogs' && homework.trainingType !== 'lab_report' && homework.processName ? (
          <button type="button" className="hwb-btn hwb-btn-secondary" onClick={() => onStartTraining?.(homework)}>
            {t('hw_start_training')}
          </button>
        ) : null}
      </div>
    </div>
  )
}
